import { ApolloCache, DocumentNode } from '@apollo/client';
import { Maybe, Node, PageInfo } from '@graphql/admin/generated/graphql.types';
import { findRootNameInQuery } from './findRootNameInQuery';
import { pagination } from './pagination';

export const relayCache = {
  addRelayEdgeInCache: <
    C extends ApolloCache<any>,
    N extends Node,
    Q extends DocumentNode,
    V extends any,
  >(
    cache: C,
    opts: {
      query: Q;
      variables?: V;
      node?: Maybe<N>;
      before?: boolean;
      transforms?: ((c: { pageInfo: PageInfo; edges?: Maybe<{ cursor: string; node: N; }[]> }) => typeof c)[];
    }
  ) => {
    const { query, variables, node, transforms, before = false } = opts;

    if (node) {
      type NodeConnection = ReturnType<NonNullable<typeof opts.transforms>[0]>;
      const queryFieldName = findRootNameInQuery(query);
      const cached = cache.readQuery<Record<string, NodeConnection>>({ query, variables });
      if (cached) {
        let updated = pagination.mergeRelayEdge(
          { cursor: pagination.encodeCursor(node._id), node },
          cached[queryFieldName],
          { before }
        );
        if (transforms) {
          for (const t of transforms) { updated = t(updated); }
        }
        cache.writeQuery({
          query,
          variables,
          data: {
            [queryFieldName]: updated
          }
        });
      }
      return cached;
    }
  },
  removeRelayEdgeFromCache: <
    C extends ApolloCache<any>,
    Q extends DocumentNode,
    V extends any,
  >(cache: C, opts: {
    query: Q;
    variables?: V;
    id?: Maybe<string>;
    ids?: Maybe<string[]>;
  }) => {
    const { query, variables } = opts;
    type NodeConnection = { pageInfo: PageInfo; edges?: Maybe<{ cursor: string; node: Node; }[]>; };
    const queryFieldName = findRootNameInQuery(query);
    const idsToRemove = opts.ids ? opts.ids : (opts.id ? [opts.id] : []);
    const cached = cache.readQuery<Record<string, NodeConnection>>({ query, variables });
    if (cached) {
      let updated = { ...cached[queryFieldName] };
      for (const id of idsToRemove) {
        updated = pagination.spliceRelayEdgeById(id, updated);
      }
      cache.writeQuery({
        query,
        variables,
        data: { [queryFieldName]: updated }
      });
    }
    return cached;
  }
};
